import { useEffect, useState } from 'react';

import { Map, List } from 'immutable';
import CheckboxListDraggable from 'flow/components/custom-components/CheckboxListDraggable';
import useAppContext from 'flow/hooks/useAppContext';
import BaseModal from 'flow/components/ui-components/BaseModal';
import { CentralSwitchWidgetData } from 'flow/types/CentralSwitchWidgets';
import {
    AvailableWidgetsMap,
    CentralSwitchWidgetKey,
} from 'flow/utils/CentralSwitchUtils';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    saveActiveWidgets?: (activeWidgets: CentralSwitchWidgetKey[]) => void;
    activeWidgetKeys?: CentralSwitchWidgetKey[];
    availableWidgets?: AvailableWidgetsMap;
}

const CentralSwitchWidgetDialog = ({
    saveActiveWidgets,
    activeWidgetKeys,
    availableWidgets,
}: Props) => {
    const { i18next } = useAppContext();

    const [selectedWidgets, setSelectedWidgets] = useState(activeWidgetKeys);
    //This is needed for the retailer logo to show up in the dialog for the first time.
    //It gets added a little later than the activeWidgets that are already there.
    useEffect(() => {
        setSelectedWidgets(activeWidgetKeys);
    }, [activeWidgetKeys]);

    const getTranslationKey = (widgetKey: CentralSwitchWidgetKey) =>
        availableWidgets[widgetKey]?.translationKey;

    const createCheckBoxListItem = (
        widgetKey: CentralSwitchWidgetKey,
        selected: boolean,
    ) => {
        const translationKey = getTranslationKey(widgetKey);

        let disabled = false;
        if (translationKey === 'central_switch.logo') {
            disabled = true;
        }
        return Map({
            key: widgetKey,
            value: i18next.t(translationKey),
            selected,
            disabled,
        });
    };
    const getAllWidgetsWithSelectedFirst = () =>
        List(selectedWidgets)
            .filter(
                (widgetKey: CentralSwitchWidgetKey) =>
                    widgetKey in availableWidgets,
            )
            // Cannot assume widget exists as there's a possibility it has been removed since the central switch was created

            .map((widgetKey: CentralSwitchWidgetKey) =>
                createCheckBoxListItem(widgetKey, true),
            )
            .concat(
                // Get the widgets that are not selected and push to list
                Object.keys(availableWidgets)
                    .filter(
                        (widgetKey: CentralSwitchWidgetKey) =>
                            !selectedWidgets.includes(widgetKey),
                    )
                    .filter(
                        (widgetKey: CentralSwitchWidgetKey) =>
                            getTranslationKey(widgetKey) !==
                            'central_switch.logo',
                    )
                    .sort((widgetKey: CentralSwitchWidgetKey) =>
                        i18next.t(getTranslationKey(widgetKey)),
                    )
                    .map((widgetKey: CentralSwitchWidgetKey) =>
                        createCheckBoxListItem(widgetKey, false),
                    ),
            );

    const updateWidgets = (
        updatedWidgetList: TypedMap<CentralSwitchWidgetData>[],
    ) => {
        setSelectedWidgets(
            updatedWidgetList
                .filter((widget) => widget.get('selected'))
                .map((checkBoxListItem) => checkBoxListItem.get('key')),
        );
    };

    const sortedWidgets = getAllWidgetsWithSelectedFirst();

    return (
        <BaseModal
            open={true}
            header={i18next.t('central_switch.choose')}
            onClose={() => setSelectedWidgets(activeWidgetKeys)}
            onSubmit={() => saveActiveWidgets(selectedWidgets)}
        >
            <CheckboxListDraggable
                items={sortedWidgets}
                onItemsUpdate={updateWidgets}
            />
        </BaseModal>
    );
};

export default CentralSwitchWidgetDialog;
